import { Grid, Stack, Typography } from '@mui/material';
import PortalModal from '../../common/PortalModal';
import { useState } from 'react';
import { FormAutoComplete } from '../../common/Form/FormAutoComplete';
import { CODECS_TIME_RANGE } from '../../common/constants';
import { FormRadioButton } from '../../common/Form/FormRadioButton';
import { FormDatePicker } from '../../common/Form/FormDatePicker';
import dayjs from 'dayjs';

export default function AddCodecsModal({
  isOpen,
  toggleModalOpen,
  id,
  onConfirm,
  codecs,
  course,
  isBulkAction = false,
  isSmall = false,
}) {
  const courseStartDate = dayjs(course?.START_DATE_2);
  const courseEndDate = dayjs(course?.END_DATE_2);
  const currentDate = dayjs(new Date());
  const currentStartDate = courseStartDate > currentDate ? courseStartDate : currentDate;

  const [selectedCodecs, setSelectedCodecs] = useState([]);
  const [timeRange, setTimeRange] = useState(CODECS_TIME_RANGE.DATE_RANGE);
  const [startDate, setStartDate] = useState(isBulkAction ? null : currentStartDate);
  const [endDate, setEndDate] = useState(isBulkAction ? null : courseEndDate);
  const [specificDate, setSpecificDate] = useState(isBulkAction ? null : currentStartDate);

  const onTimeRangeChange = (value) => {
    setTimeRange(value);
    switch (value) {
      case CODECS_TIME_RANGE.SPECIFIC_DATE: {
        setSpecificDate(isBulkAction ? null : currentStartDate);
        setStartDate(null);
        setEndDate(null);
        break;
      }
      case CODECS_TIME_RANGE.REST_OF_THE_SEMESTER: {
        setSpecificDate(null);
        setStartDate(null);
        setEndDate(null);
        break;
      }
      case CODECS_TIME_RANGE.DATE_RANGE:
      default: {
        setSpecificDate(null);
        setStartDate(isBulkAction ? null : currentStartDate);
        setEndDate(isBulkAction ? null : courseEndDate);
      }
    }
  };

  const getTimeRangeData = () => {
    switch (timeRange) {
      case CODECS_TIME_RANGE.SPECIFIC_DATE: {
        return { startDate: specificDate, endDate: specificDate };
      }
      case CODECS_TIME_RANGE.REST_OF_THE_SEMESTER: {
        return {
          startDate: currentStartDate,
          endDate: courseEndDate,
        };
      }
      case CODECS_TIME_RANGE.DATE_RANGE:
      default: {
        return { startDate, endDate };
      }
    }
  };

  return (
    <PortalModal
      title={'Add Codecs'}
      maxWidth="md"
      isOpen={isOpen}
      onClose={toggleModalOpen}
      isCloseable
      fullWidth
      isFullScreen={isSmall}
      primaryButtonTitle="Add"
      isPrimaryButtonDisabled={!selectedCodecs.length}
      onPrimaryClick={() => {
        const codecsData = getTimeRangeData() ?? {};
        codecsData.codecs = selectedCodecs;
        onConfirm(codecsData);
      }}
      secondaryButtonTitle="Cancel"
      onSecondaryClick={toggleModalOpen}
      id={`${id}__model`}
    >
      <Grid
        container
        id={`${id}__grid_container`}
        direction="column"
        spacing={2}
        sx={{
          justifyContent: 'center',
        }}
      >
        {!isBulkAction && course?.CRN && (
          <Grid item id={`${id}__grid_item_text`} xs={12} xl={3}>
            <Typography id={`${id}__text`} variant="small">
              {`Select the codecs to add to the course the meetings of ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}`}
            </Typography>
          </Grid>
        )}
        <Grid item id={`${id}__grid_item_codecs`} xs={12} xl={3}>
          <FormAutoComplete
            value={selectedCodecs}
            id={`${id}__selectAutoComplete_codecs`}
            label="Codecs"
            labelGridProps={{ xs: 12, md: 2 }}
            inputGridProps={{ xs: 12, md: 10 }}
            options={codecs}
            required={true}
            getOptionLabel={(option) => option.SYSTEM_NAME}
            placeholder="Search by System Name"
            onChange={(e, value) => setSelectedCodecs(value)}
            autocompleteProps={{ multiple: true }}
            ariaLabel="Codecs Selection"
          />
        </Grid>

        <Grid item id={`${id}__grid_item_dates`} xs={12} xl={3}>
          <Grid container id={`${id}__grid_container_dates`} spacing={{ xs: 0, sm: 1 }}>
            <Grid
              item
              id={`${id}__grid_item_label_dates`}
              xs={12}
              md={2}
              textAlign={{ xs: 'start', md: 'end' }}
            >
              <Typography className="myOdu__label" id={`${id}__text_label_dates`}>
                Dates
              </Typography>
            </Grid>

            <Grid item id={`${id}__grid_item_dates_options`} xs={12} md={10} alignItems="center">
              <Stack
                id={`${id}__stack_dateRange`}
                direction={'row'}
                spacing={1}
                alignItems={'center'}
              >
                <FormRadioButton
                  id={`${id}__radioButton_dateRange`}
                  options={[
                    {
                      ariaLabel: 'Date Range',
                      value: CODECS_TIME_RANGE.DATE_RANGE,
                      checked: timeRange === CODECS_TIME_RANGE.DATE_RANGE,
                    },
                  ]}
                  labelGridProps={{ xs: 0 }}
                  inputGridProps={{ xs: 0, m: 0, p: 0 }}
                  onChange={(e) => onTimeRangeChange(e.target.value)}
                />
                <Typography
                  sx={{ whiteSpace: 'nowrap' }}
                  variant="small"
                  id={`${id}__text_label_dateRange`}
                >
                  Date Range
                </Typography>
              </Stack>

              {timeRange === CODECS_TIME_RANGE.DATE_RANGE && (
                <Grid id={`${id}__grid_container_dateRange`} container spacing={{ xs: 0, sm: 1 }}>
                  <Grid item id={`${id}__grid_item_dateRange_startDate`} xs={12} md={10}>
                    <FormDatePicker
                      id={`${id}__datePicker_startDate`}
                      value={startDate}
                      onChange={setStartDate}
                      label={'Start Date'}
                      labelGridProps={{
                        xs: 12,
                        md: 2,
                      }}
                      inputGridProps={{
                        xs: 12,
                        md: 10,
                      }}
                      minDate={!isBulkAction ? courseStartDate : undefined}
                      maxDate={!isBulkAction ? courseEndDate : undefined}
                    />
                  </Grid>
                  <Grid item id={`${id}__grid_item_dateRange_endDate`} xs={12} md={10}>
                    <FormDatePicker
                      id={`${id}__datePicker_endDate`}
                      value={endDate}
                      onChange={setEndDate}
                      label={'End Date'}
                      labelGridProps={{
                        xs: 12,
                        md: 2,
                      }}
                      inputGridProps={{
                        xs: 12,
                        md: 10,
                      }}
                      minDate={!isBulkAction ? courseStartDate : undefined}
                      maxDate={!isBulkAction ? courseEndDate : undefined}
                      error={endDate?.diff(startDate, 'day') < 0}
                      errorText={'End date must be after start'}
                    />
                  </Grid>
                </Grid>
              )}

              <Stack
                id={`${id}__stack_specificDate`}
                direction={'row'}
                spacing={1}
                alignItems={'center'}
              >
                <FormRadioButton
                  id={`${id}__radioButton_specificDate`}
                  options={[
                    {
                      ariaLabel: 'Specific Date',
                      value: CODECS_TIME_RANGE.SPECIFIC_DATE,
                      checked: timeRange === CODECS_TIME_RANGE.SPECIFIC_DATE,
                    },
                  ]}
                  labelGridProps={{ xs: 0 }}
                  inputGridProps={{ xs: 0, m: 0, p: 0 }}
                  onChange={(e) => onTimeRangeChange(e.target.value)}
                />
                <Typography
                  sx={{ whiteSpace: 'nowrap' }}
                  variant="small"
                  id={`${id}__text_label_specificDate`}
                >
                  Specific Date
                </Typography>
              </Stack>

              {timeRange === CODECS_TIME_RANGE.SPECIFIC_DATE && (
                <Grid
                  container
                  id={`${id}__grid_container_specificDate`}
                  spacing={{ xs: 0, sm: 1 }}
                >
                  <Grid item id={`${id}__grid_item_specificDate_date`} xs={12} md={10}>
                    <FormDatePicker
                      id={`${id}__datePicker_specificDate`}
                      value={specificDate}
                      onChange={setSpecificDate}
                      label={'Specific Date'}
                      labelGridProps={{
                        xs: 12,
                        md: 2,
                      }}
                      inputGridProps={{
                        xs: 12,
                        md: 10,
                      }}
                      minDate={!isBulkAction ? courseStartDate : undefined}
                      maxDate={!isBulkAction ? courseEndDate : undefined}
                    />
                  </Grid>
                </Grid>
              )}

              <Stack
                id={`${id}__stack_restOfSemester`}
                direction={'row'}
                spacing={1}
                alignItems={'center'}
              >
                <FormRadioButton
                  id={`${id}__radioButton_restOfSemester`}
                  options={[
                    {
                      ariaLabel: 'Rest of the semester',
                      value: CODECS_TIME_RANGE.REST_OF_THE_SEMESTER,
                      checked: timeRange === CODECS_TIME_RANGE.REST_OF_THE_SEMESTER,
                    },
                  ]}
                  labelGridProps={{ xs: 0 }}
                  inputGridProps={{ xs: 0, m: 0, p: 0 }}
                  onChange={(e) => onTimeRangeChange(e.target.value)}
                />
                <Typography
                  sx={{ whiteSpace: 'nowrap' }}
                  variant="small"
                  id={`${id}__text_label_restOfSemester`}
                >
                  Rest of the semester
                </Typography>
              </Stack>

              {timeRange === CODECS_TIME_RANGE.REST_OF_THE_SEMESTER && (
                <Grid
                  container
                  id={`${id}__grid_container_restOfSemester`}
                  spacing={{ xs: 0, sm: 1 }}
                >
                  <Grid item id={`${id}__grid_item_restOfSemester`} xs={12} md={10}>
                    <Typography id={`${id}__text_restOfSemester`}>
                      {`You are assigning this codec from today to
                                    ${isBulkAction ? '' : courseEndDate?.format('YYYY/MM/DD')}`}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PortalModal>
  );
}
